import React, { useState } from 'react';
import { useReaderVersion } from './hooks/useReaderVersion';
import {
  ReaderVersionList,
  CreateReaderVersionModal,
  ReaderVersionFilters,
  PlatformStatsCard
} from './components';
import { ReaderVersion } from './types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';

const ReaderVersionDashboard: React.FC = () => {
  const {
    versions,
    platformStats,
    pagination,
    filters,
    isLoading,
    error,
    createVersion,
    updateVersion,
    deleteVersion,
    applyFilters,
    clearFilters,
    loadVersions,
    setError
  } = useReaderVersion();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ReaderVersion | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<ReaderVersion | null>(null);
  const [viewingVersion, setViewingVersion] = useState<ReaderVersion | null>(null);

  // Handle create version
  const handleCreateVersion = () => {
    setEditingVersion(null);
    setIsModalOpen(true);
  };

  // Handle edit version
  const handleEditVersion = (version: ReaderVersion) => {
    setEditingVersion(version);
    setIsModalOpen(true);
  };

  // Handle view version details
  const handleViewVersion = (version: ReaderVersion) => {
    setViewingVersion(version);
  };

  // Handle delete version
  const handleDeleteVersion = (version: ReaderVersion) => {
    setShowDeleteConfirm(version);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      await deleteVersion(showDeleteConfirm.id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting version:', error);
    }
  };

  // Handle modal submit
  const handleModalSubmit = async (data: Partial<ReaderVersion>) => {
    try {
      setIsSubmitting(true);
      
      if (editingVersion) {
        await updateVersion(editingVersion.id, data);
      } else {
        await createVersion(data);
      }
      
      setIsModalOpen(false);
      setEditingVersion(null);
    } catch (error) {
      // Error is handled by the hook
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    loadVersions(page);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Reader Version</h1>
          <p className="text-gray-600 mt-1">
            Quản lý phiên bản ứng dụng reader trên các nền tảng khác nhau
          </p>
        </div>
        <button
          onClick={handleCreateVersion}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Tạo phiên bản mới</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Có lỗi xảy ra</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setError(null)}
                  className="text-sm font-medium text-red-800 hover:text-red-600"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters */}
          <ReaderVersionFilters
            filters={filters}
            onFiltersChange={applyFilters}
            onClearFilters={clearFilters}
          />

          {/* Versions List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Danh sách phiên bản ({pagination.total} phiên bản)
              </h2>
            </div>

            <ReaderVersionList
              versions={versions}
              isLoading={isLoading}
              onEdit={handleEditVersion}
              onDelete={handleDeleteVersion}
              onView={handleViewVersion}
            />

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="mt-6 flex justify-center">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        page === pagination.current_page
                          ? 'text-blue-600 bg-blue-50 border border-blue-300'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sau
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <PlatformStatsCard stats={platformStats} isLoading={isLoading} />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <CreateReaderVersionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingVersion(null);
        }}
        onSubmit={handleModalSubmit}
        editingVersion={editingVersion}
        isLoading={isSubmitting}
      />

      {/* Version Details Dialog: nội dung để đọc và copy nên không tự đóng */}
      <Dialog open={!!viewingVersion} onOpenChange={(open) => { if (!open) setViewingVersion(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Version Details</DialogTitle>
          </DialogHeader>
          {viewingVersion && (
            <dl className="space-y-2 text-sm text-gray-700">
              <div><dt className="inline font-medium">Platform: </dt><dd className="inline">{viewingVersion.platform}</dd></div>
              <div><dt className="inline font-medium">Version: </dt><dd className="inline">{viewingVersion.version}</dd></div>
              <div><dt className="inline font-medium">Mandatory: </dt><dd className="inline">{viewingVersion.mandatory ? 'Yes' : 'No'}</dd></div>
              <div>
                <dt className="font-medium">Release Notes:</dt>
                <dd className="whitespace-pre-line">{viewingVersion.release_notes || 'None'}</dd>
              </div>
              <div>
                <dt className="font-medium">Download URL:</dt>
                <dd className="break-all select-all">{viewingVersion.download_url || 'None'}</dd>
              </div>
            </dl>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingVersion(null)}>Đóng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Xác nhận xóa phiên bản
              </h3>
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa phiên bản <strong>{showDeleteConfirm.version}</strong> của nền tảng <strong>{showDeleteConfirm.platform}</strong>?
                <br />
                <span className="text-red-600 text-sm">Hành động này không thể hoàn tác.</span>
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReaderVersionDashboard;
